// Reports — P&L / Balance Sheet / A/R Aging / Transactions with date presets.
// Renders the server-flattened sections (indent by depth, mono amounts).
import React, { useState } from "react";
import { C, font, mono } from "../../../lib/styles.js";
import { useQbo } from "../lib/useQbo.js";
import {
  ErrorPanel, Loading, RefreshButton, Seg, money, panel, presetRange, td, tdNum, th, today,
} from "./common.jsx";

const REPORTS = [
  { value: "report_pnl", label: "Profit & Loss" },
  { value: "report_balance_sheet", label: "Balance Sheet" },
  { value: "report_ar_aging", label: "A/R Aging" },
  { value: "report_transactions", label: "Transactions" },
];
const PRESETS = [
  { label: "This month", value: "this_month" },
  { label: "Last month", value: "last_month" },
  { label: "This quarter", value: "this_quarter" },
  { label: "YTD", value: "ytd" },
  { label: "Custom", value: "custom" },
];

const selStyle = {
  font: "inherit", fontSize: 12.5, padding: "7px 10px",
  border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface,
};
const dateInp = { ...selStyle, padding: "6px 8px" };
const rowStyle = (depth, kind) => ({
  display: "flex", justifyContent: "space-between", padding: "7px 10px",
  fontSize: 12.5, borderBottom: `1px solid ${C.border}`,
  paddingLeft: 10 + depth * 18,
  fontWeight: kind === "summary" ? 500 : 300,
});

const SectionedReport = ({ report }) => (
  <div style={{ ...panel, padding: 0 }}>
    {report.shapeWarning && (
      <div style={{ padding: "8px 10px", fontSize: 11.5, color: "#8a5a06", background: "#FDF3E1" }}>
        Some report rows had an unexpected shape and may be missing.
      </div>
    )}
    {report.sections.map((s) => (
      <React.Fragment key={s.name}>
        <div style={{ ...rowStyle(0, "head"), fontWeight: 500, background: C.surfaceAlt }}>
          <span>{s.name}</span><span />
        </div>
        {s.rows.map((r, i) => (
          <div key={`${r.name}-${i}`} style={rowStyle(r.depth + 1, r.kind)}>
            <span>{r.name}</span>
            <span style={{ fontFamily: mono, fontSize: 12 }}>{r.amount === null ? "" : money(r.amount)}</span>
          </div>
        ))}
        {s.total !== null && (
          <div style={{ ...rowStyle(1, "summary") }}>
            <span>Total {s.name}</span>
            <span style={{ fontFamily: mono, fontSize: 12 }}>{money(s.total)}</span>
          </div>
        )}
      </React.Fragment>
    ))}
    {report.grandTotals.map((g) => (
      <div key={g.label} style={{ ...rowStyle(0, "summary"), borderTop: `1px solid ${C.text}`, borderBottom: "none" }}>
        <span>{g.label}</span>
        <span style={{ fontFamily: mono, fontSize: 12 }}>{money(g.amount)}</span>
      </div>
    ))}
    {report.sections.length === 0 && report.grandTotals.length === 0 && (
      <div style={{ padding: 18, fontSize: 12.5, color: C.muted, textAlign: "center" }}>No rows for this period.</div>
    )}
  </div>
);

const AgingReport = ({ data }) => (
  <div style={{ ...panel, padding: 0, overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th style={th}>Customer</th>
          {(data.bucketLabels ?? []).map((b) => <th key={b} style={{ ...th, textAlign: "right" }}>{b}</th>)}
        </tr>
      </thead>
      <tbody>
        {(data.customers ?? []).map((c) => (
          <tr key={c.name}>
            <td style={td}>{c.name}</td>
            {c.buckets.map((v, i) => <td key={i} style={tdNum}>{v === null ? "" : money(v)}</td>)}
          </tr>
        ))}
        <tr>
          <td style={{ ...td, fontWeight: 500, borderBottom: "none" }}>Total A/R</td>
          <td style={{ ...tdNum, fontWeight: 500, borderBottom: "none" }} colSpan={(data.bucketLabels ?? []).length}>
            {money(data.totalAR)}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
);

const TransactionsReport = ({ data }) => (
  <div style={{ ...panel, padding: 0, overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>{(data.columns ?? []).map((c, i) => (
          <th key={i} style={{ ...th, textAlign: i === (data.columns.length - 1) ? "right" : "left" }}>{c || "—"}</th>
        ))}</tr>
      </thead>
      <tbody>
        {(data.rows ?? []).map((r, i) => (
          <tr key={i}>
            <td style={td}>{r.label}</td>
            {r.cols.map((v, j) => {
              const isLast = j === r.cols.length - 1;
              const n = isLast && v !== "" && Number.isFinite(Number(String(v).replace(/,/g, "")))
                ? Number(String(v).replace(/,/g, "")) : null;
              return (
                <td key={j} style={isLast ? tdNum : td}>
                  {n !== null ? money(n) : v}
                </td>
              );
            })}
          </tr>
        ))}
        {(data.rows ?? []).length === 0 && (
          <tr><td style={{ ...td, color: C.muted, textAlign: "center" }} colSpan={(data.columns ?? []).length || 1}>No transactions in this period.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export default function Reports({ ctx }) {
  const [report, setReport] = useState("report_pnl");
  const [preset, setPreset] = useState("this_month");
  const [custom, setCustom] = useState({ start: presetRange("this_month").start, end: today() });

  const range = preset === "custom" ? custom : presetRange(preset);
  const params =
    report === "report_balance_sheet" ? { asOf: range.end } :
    report === "report_ar_aging" ? { asOf: range.end } :
    { start: range.start, end: range.end };

  const { data, loading, error, refresh } = useQbo(ctx, report, params);

  return (
    <div style={{ fontFamily: font }}>
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <select style={selStyle} value={report} onChange={(e) => setReport(e.target.value)}>
          {REPORTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <Seg options={PRESETS} value={preset} onChange={setPreset} />
        {preset === "custom" && (
          <>
            <input type="date" style={dateInp} value={custom.start}
              onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))} />
            <span style={{ color: C.muted, fontSize: 12 }}>→</span>
            <input type="date" style={dateInp} value={custom.end}
              onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))} />
          </>
        )}
        <span style={{ marginLeft: "auto" }}><RefreshButton onClick={refresh} /></span>
      </div>
      {loading && !data ? <Loading label="Running the report…" /> :
        error ? <ErrorPanel error={error} onRetry={refresh} /> :
        report === "report_ar_aging" ? <AgingReport data={data} /> :
        report === "report_transactions" ? <TransactionsReport data={data} /> :
        <SectionedReport report={data} />}
    </div>
  );
}
